import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { CONTACT_INFO_DETAILS_STATUS } from '../../../../consts/enums';
import { CheckCircleSolid } from '../../../../icons/check-circle-solid';
import { Download } from '../../../../icons/download';
import { FilledAlertError } from '../../../../icons/filled-alert-error';
import { CustomTooltip } from '../../../CustomTooltip';
import { WarningFilled } from '../../../../icons/warning-filled';
import STRING_KEYS from '../../../../language/keys';
import { ContactInfoStatus } from './ContactInfoStatus';

type ContactInfoLoadStateProps = {
  status: ContactInfoStatus | '',
  errorMessage: string,
}

const COLORS = {
  GRAY: '#6b7280',
  SUCCESS: '#43c6b7',
  ERROR: '#da6868',
  WARNING: '#ffbf4c',
};

export const ContactInfoLoadState = ({
  status,
  errorMessage,
}: ContactInfoLoadStateProps):JSX.Element | null => {
  const { t: translate } = useTranslation();

  switch (status) {
    case CONTACT_INFO_DETAILS_STATUS.IN_PROGRESS: {
      return (
        <ContactInfoStatus
          color={ COLORS.GRAY }
          description={ `${translate(STRING_KEYS.SEARCHING_DATABASE)}...` }
          icon={ (
            <Download width="22" height="22" />
          ) }
          variant="body2"
        />
      );
    }
    case CONTACT_INFO_DETAILS_STATUS.FINISHED: {
      return (
        <ContactInfoStatus
          color={ COLORS.SUCCESS }
          description={ translate(STRING_KEYS.CONTACT_INFO_STATUS_DONE) }
          icon={ (
            <CheckCircleSolid viewBox="-1 -1 18 18" fontSize="small" />
          ) }
          variant="body2"
        />
      );
    }
    case CONTACT_INFO_DETAILS_STATUS.FAILED: {
      return (
        <Box sx={ {
          mx: 3,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          marginTop: -1,
        } }
        title={ errorMessage }
        >
          <CustomTooltip title={ errorMessage }>
            <ContactInfoStatus
              color={ COLORS.ERROR }
              description={ errorMessage }
              icon={ (
                <Box sx={ { marginTop: 1.2, marginRight: -0.6 } }>
                  <FilledAlertError viewBox="1 1 22 22" fontSize="small" />
                </Box>
              ) }
              variant="caption"
            />
          </CustomTooltip>
        </Box>
      );
    }
    case CONTACT_INFO_DETAILS_STATUS.CONTACTS_NOT_FOUND: {
      return (
        <ContactInfoStatus
          color={ COLORS.WARNING }
          description={ translate(STRING_KEYS.CONTACT_INFO_STATUS_NO_RESULTS) }
          icon={ <WarningFilled color="warning" fontSize="small" /> }
          variant="body2"
        />
      );
    }

    default: {
      return null;
    }
  }
};
